/**
 * <ReleaseBanner /> — top-of-page announcement linked to the current
 * active release (most-recent published, audience-matched, not yet
 * dismissed by this user).
 *
 * Shown on /home (after login). One banner at a time. Per-user
 * dismissal persists across sessions via /api/releases/{id}/dismiss.
 *
 * Designed to be drop-in: mount it anywhere and it self-fetches +
 * self-hides when there's nothing to show.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Sparkles, X, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReleaseBanner = () => {
  const [release, setRelease] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/releases/active`)
      .then((r) => {
        if (!cancelled) setRelease(r.data?.release || null);
      })
      .catch(() => { /* silent — no banner is a fine default */ });
    return () => { cancelled = true; };
  }, []);

  const dismiss = async () => {
    if (!release) return;
    setHidden(true);
    try {
      await axios.post(`${API}/releases/${release.release_id}/dismiss`);
    } catch {
      // If the dismissal call fails we still keep the banner hidden for
      // this session — re-opening the tab will re-show it, which is fine.
    }
  };

  if (!release || hidden) return null;

  const isExternal = release.cta_url && /^https?:\/\//i.test(release.cta_url);

  return (
    <div
      className="relative bg-gradient-to-r from-[#0052FF] to-[#003ECC] text-white px-4 sm:px-6 py-4 rounded-xl shadow-sm overflow-hidden mb-6"
      data-testid="release-banner"
    >
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 max-w-6xl mx-auto">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/70">
                New
              </span>
              <h3 className="text-base sm:text-lg font-bold tracking-tight" data-testid="release-banner-title">
                {release.title}
              </h3>
            </div>
            <p className="text-sm text-white/85 mt-0.5" data-testid="release-banner-summary">
              {release.summary}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 self-end sm:self-auto">
          {release.cta_label && release.cta_url && (
            isExternal ? (
              <a
                href={release.cta_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-white text-[#0052FF] text-sm font-semibold hover:bg-white/95 transition-colors"
                data-testid="release-banner-cta"
              >
                {release.cta_label}
                <ArrowRight className="w-3.5 h-3.5" />
              </a>
            ) : (
              <Link
                to={release.cta_url}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-white text-[#0052FF] text-sm font-semibold hover:bg-white/95 transition-colors"
                data-testid="release-banner-cta"
              >
                {release.cta_label}
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            )
          )}
          <button
            type="button"
            onClick={dismiss}
            className="w-8 h-8 rounded-lg hover:bg-white/15 flex items-center justify-center transition-colors"
            aria-label="Dismiss"
            data-testid="release-banner-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReleaseBanner;
